import { GS1_AI } from '@myplayer/gs1';
import { type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import { formatWorkspaceName } from './auth-session.js';
import { QrCustomiserCard, type SaveBrandQrStyleCallableFn } from './qr-customiser.js';
import { type QrStyleConfig } from './qr-style-rules.js';
import { resolveEffectiveHost } from './use-workspace-identifiers.js';

export interface BrandQrSectionProps {
  readonly resolverHost?: string | undefined;
  readonly sampleBrandGtin?: string | undefined;
  readonly brandName?: string | undefined;
  readonly initialBrandQrStyle?: QrStyleConfig | undefined;
  readonly onSaveBrandQrStyle?: ((style: QrStyleConfig) => void) | undefined;
  readonly onUpdateWorkspaceCallable?: SaveBrandQrStyleCallableFn | undefined;
}

const DEFAULT_BRAND_SAMPLE_GTIN = '09506000134352';

export function BrandQrSection({
  resolverHost,
  sampleBrandGtin = DEFAULT_BRAND_SAMPLE_GTIN,
  brandName,
  initialBrandQrStyle,
  onSaveBrandQrStyle,
  onUpdateWorkspaceCallable,
}: BrandQrSectionProps): ReactElement {
  const { session, availableWorkspaces } = useAuth();
  const effectiveHost = resolveEffectiveHost(resolverHost);
  const workspaceSlug = session?.activeWorkspaceId ?? 'brand';
  const workspaceName =
    availableWorkspaces.find((ws) => ws.workspaceId === workspaceSlug)?.name ??
    formatWorkspaceName(undefined, workspaceSlug);

  return (
    <QrCustomiserCard
      cardTestId="brand-qr-card"
      title="Brand QR customiser"
      subtitle={`QR code style and print preview for ${workspaceName}`}
      host={effectiveHost}
      ai={GS1_AI.GTIN}
      primaryKey={sampleBrandGtin}
      fileSlug={`brand-${workspaceSlug}`}
      workspaceId={workspaceSlug}
      {...(brandName !== undefined ? { brandName } : {})}
      {...(initialBrandQrStyle !== undefined ? { initialStyle: initialBrandQrStyle } : {})}
      {...(onSaveBrandQrStyle !== undefined ? { onSaveStyledDesign: onSaveBrandQrStyle } : {})}
      {...(onUpdateWorkspaceCallable !== undefined ? { onUpdateWorkspaceCallable } : {})}
    />
  );
}
